import { Contact } from "@data-phone/react-generic/dist/components/Forms/Contacts";
import { formatContactName } from "./contacts";
import { formatPhoneNumber, phoneNumberFormatE164 } from "./phoneLib";

export type ContactsByPhone = Map<string, Contact>;

export function createContactsByPhone(contacts: Contact[]): ContactsByPhone {
  const contactsByPhone: ContactsByPhone = new Map();

  contacts.forEach((contact) => {
    contact.phones.forEach((phone) => {
      const normalizedPhone = phoneNumberFormatE164(phone.phone_number);
      if (normalizedPhone) {
        contactsByPhone.set(normalizedPhone, contact);
      }
    });
  });

  return contactsByPhone;
}

export function participantName(
  participant: string,
  contacts: Contact[] | ContactsByPhone
): string {
  const normalizedParticipant = phoneNumberFormatE164(participant);
  const contact =
    contacts instanceof Map
      ? contacts.get(normalizedParticipant)
      : contacts.find((item) =>
          item.phones.some(
            (phone) =>
              phoneNumberFormatE164(phone.phone_number) ===
              normalizedParticipant
          )
        );

  return contact
    ? formatContactName(contact)
    : (formatPhoneNumber(participant) as string);
}

export function participantLabel(
  participant: string,
  contacts: Contact[] | ContactsByPhone
): string {
  const name = participantName(participant, contacts);
  const phoneNumber = formatPhoneNumber(participant) as string;

  return name === phoneNumber ? phoneNumber : `${name} · ${phoneNumber}`;
}

export function getConversationParticipantSummary(
  participants: string[],
  contacts: Contact[] | ContactsByPhone,
  visibleParticipantCount = 2
): {
  names: string;
  additionalParticipantCount: number;
  additionalParticipantNames: string[];
} {
  const participantNames = participants.map((participant) =>
    participantName(participant, contacts)
  );
  const additionalParticipantNames = participantNames.slice(
    visibleParticipantCount
  );

  return {
    names: participantNames.slice(0, visibleParticipantCount).join(", "),
    additionalParticipantCount: additionalParticipantNames.length,
    additionalParticipantNames,
  };
}

export function getConversationTitle(
  participants: string[],
  contacts: Contact[]
): string {
  const { names, additionalParticipantCount } =
    getConversationParticipantSummary(participants, contacts);

  if (!additionalParticipantCount) {
    return names;
  }

  return `${names} +${additionalParticipantCount}`;
}

export function getConversationSubtitle(participants: string[]): string {
  return participants
    .map((participant) => formatPhoneNumber(participant) as string)
    .join(", ");
}

export function getConversationAvatarText(participants: string[]): string {
  if (participants.length > 1) return String(participants.length);
  const participant = participants[0] || "";
  return participant[participant.length - 1] || "0";
}
